import { ENV } from "@/env";
import type { LightningBinaryRequest } from "@/layers/lightning-binary/LightningBinaryRequest";

function toUtcSecondPrecision(value: LightningBinaryRequest["datetime"]): string {
  const iso = value.toUTC().toISO({ suppressMilliseconds: true, includeOffset: true });
  if (!iso) {
    throw new RangeError("Lightning request datetime is invalid");
  }
  return iso;
}

function canonicalTypeFilter(filter: LightningBinaryRequest["typeFilter"]): number[] {
  return [...new Set(filter ?? [])].sort((left, right) => left - right);
}

function resolveBaseUrl(baseUrl: string): URL {
  // A hostname without a scheme is otherwise interpreted as a path by URL,
  // which would make `iceberg.meteomatics.com` resolve below localhost.
  const normalizedBaseUrl = /^(?:[a-z\d](?:[a-z\d-]*[a-z\d])?\.)+[a-z]{2,}(?::\d+)?$/i.test(baseUrl)
    ? `https://${baseUrl}`
    : baseUrl;
  return new URL(normalizedBaseUrl, globalThis.location?.origin ?? "http://localhost");
}

export function buildLightningBinaryUrl(request: LightningBinaryRequest, baseUrl: string = ENV.baseUrlIceberg): URL {
  if (
    !Number.isInteger(request.width) ||
    !Number.isInteger(request.height) ||
    request.width < 1 ||
    request.height < 1 ||
    request.width > 8192 ||
    request.height > 8192
  ) {
    throw new RangeError("Lightning resolution must use integer dimensions within 1..8192");
  }
  if (!Number.isFinite(request.timeDuration.as("milliseconds")) || request.timeDuration.as("milliseconds") <= 0) {
    throw new RangeError("Lightning request duration must be positive");
  }
  const base = resolveBaseUrl(baseUrl);
  const start = request.datetime.minus(request.timeDuration);
  const duration = request.timeDuration.toISO();
  if (!duration) {
    throw new RangeError("Lightning request duration is invalid");
  }

  const bbox = `${request.area.north},${request.area.west}_${request.area.south},${request.area.east}`;
  const range = `${toUtcSecondPrecision(start)}${duration}`;
  const basePath = base.pathname.replace(/\/+$/, "");
  base.pathname = `${basePath}/api/v1/lightning/${bbox}/${range}/${request.width}x${request.height}/data.bin`;
  base.search = "";
  base.searchParams.set("wire_version", String(request.wireVersion));
  base.searchParams.set("aggregation", request.aggregation);

  const filter = canonicalTypeFilter(request.typeFilter);
  if (filter.length > 0) {
    base.searchParams.set("filter", filter.join(","));
  }
  if (request.observedOnly) {
    base.searchParams.set("obs", "true");
  }
  return base;
}
